import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Heading } from '@ctw/shared/components/heading';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { DeleteCachedSummaryButton } from '@ctw/shared/content/summarization/delete-cached-summary-button';
import { PatientSummarizationStatusCard } from '@ctw/shared/content/summarization/patient-summarization-status-card';
import { PatientSummarizationUsageTicker } from '@ctw/shared/content/summarization/patient-summarization-usage-ticker';
import {
  PatientSummarizationGenerationErrorBanner,
  PatientSummarizationInsufficientDataBanner,
  PatientSummarizationLimitApproachingBanner,
  PatientSummarizationLimitReachedBanner,
} from '@ctw/shared/content/summarization/patient-summarization-warning-banners';
import { PatientSummaryCard } from '@ctw/shared/content/summarization/patient-summary-card';
import { PatientSummaryJobProvider } from '@ctw/shared/content/summarization/patient-summary-job-provider';
import {
  PatientSummaryError,
  PatientSummaryGeneratingSkeleton,
  PatientSummaryInsufficientData,
  PatientSummaryLimitReached,
  PatientSummaryNoSummaryYet,
} from '@ctw/shared/content/summarization/patient-summary-state-panels';
import type { UsePatientSummariesResponse } from '@ctw/shared/content/summarization/use-patient-summaries';
import { usePatientSummaryFeedbackDrawer } from '@ctw/shared/content/summarization/use-patient-summary-feedback-drawer';
import { useUserPatientSummarizationContext } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { formatDateToLocalString } from '@ctw/shared/utils/dates';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

export interface PatientSummaryProps {
  className?: ClassName;
}

interface PatientSummaryLayoutProps {
  className?: ClassName;
  summaryResponse: UsePatientSummariesResponse;
  children: ReactNode;
}

const PatientSummaryLayout = ({
  className,
  summaryResponse,
  children,
}: PatientSummaryLayoutProps) => {
  const { patient } = usePatientContext();
  const { openPatientSummaryFeedbackDrawer } = usePatientSummaryFeedbackDrawer({
    summaryResponse,
  });

  return (
    <div className={twx(className, 'p-3')}>
      <header className={tw`flex items-center justify-between`}>
        <div className={tw`flex flex-col gap-2`}>
          <Heading level="h1" className={tw`text-content-main`}>
            Your 5-Minute Briefing On {patient.fullName}
          </Heading>
          {summaryResponse.lastRefreshed && (
            <span className="text-content-subtle text-sm">
              Last refreshed: {formatDateToLocalString(summaryResponse.lastRefreshed)}
            </span>
          )}
        </div>
        <div className={tw`flex flex-col items-end justify-end gap-2`}>
          <div className={tw`flex items-center justify-end gap-2`}>
            <DeleteCachedSummaryButton />
            <PatientSummarizationUsageTicker showHoverDetail={true} />
          </div>
          <div className={tw`flex items-center justify-end gap-1`}>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              className={tw`flex items-center justify-center border-0 px-2 shadow-none`}
              ariaLabel="Thumbs Up"
              onClick={() =>
                openPatientSummaryFeedbackDrawer({ initialFeedbackPolarity: 'positive' })
              }
            >
              <FontAwesomeIcon icon={faThumbsUp} className={tw`h-3.5 w-3.5 text-content-subtle`} />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              className={tw`flex items-center justify-center border-0 px-2 shadow-none`}
              ariaLabel="Thumbs Down"
              onClick={() =>
                openPatientSummaryFeedbackDrawer({ initialFeedbackPolarity: 'negative' })
              }
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                className={tw`h-3.5 w-3.5 text-content-subtle`}
              />
            </Button>
          </div>
        </div>
      </header>
      <HorizontalDivider thickness="thin" />
      {children}
    </div>
  );
};

export const PatientSummary = withErrorBoundary({
  boundaryName: 'PatientSummary',
  Component: ({ className }: PatientSummaryProps) => {
    const telemetry = useTelemetry();
    const { patient } = usePatientContext();
    const { upsell } = useBuilderConfig();
    const {
      activeSummaryJob,
      summarizationLimitApproaching,
      summarizationLimitReached,
      summaryResponse,
      generateSummary,
      checkCachedSummary,
      registerSummaryPageMount,
    } = useUserPatientSummarizationContext();

    // Let the context know we're on the summary page so we
    // don't show a redundant toast when the summary is done
    useEffect(() => registerSummaryPageMount(), [registerSummaryPageMount]);

    const handleGenerate = useCallback(() => {
      if (!patient.UPID) {
        return;
      }
      generateSummary(patient.UPID);
    }, [generateSummary, patient.UPID]);

    const cacheCheckedForUpidRef = useRef<string | null>(null);
    const [isCheckingCache, setIsCheckingCache] = useState(!!patient.UPID);

    // Check for cached summary and keep the loading state in sync
    useEffect(() => {
      const upid = patient.UPID;
      if (!upid) {
        setIsCheckingCache(false);
        return;
      }
      if (cacheCheckedForUpidRef.current === upid) {
        return;
      }
      cacheCheckedForUpidRef.current = upid;
      setIsCheckingCache(true);
      void checkCachedSummary(upid).finally(() => {
        if (cacheCheckedForUpidRef.current === upid) {
          setIsCheckingCache(false);
        }
      });
    }, [patient.UPID, checkCachedSummary]);

    // After the HEAD says "completed" we still need a follow-up GET (driven by the polling
    // query) to pull in the summary sections. Hold the loading state through that hydration
    // so the success card doesn't flash before the rendered summary takes over.
    const isHydratingCachedSummary =
      summaryResponse.status === 'completed' && summaryResponse.summaries.length === 0;
    const isLoadingSummary = isCheckingCache || isHydratingCachedSummary;
    const hasActiveJob = activeSummaryJob?.patientUpid === patient.UPID;

    const [presentedSummaryCount, setPresentedSummaryCount] = useState(0);

    // Per summary job, snapshot the status of every section present in the first polling
    // response that contained any summaries. Sections in that snapshot with status
    // 'completed' render instantly (the user is reloading content that was already done);
    // every other section — including ones that appear after the first observation —
    // streams in. Scoping by summaryJobId resets the snapshot when a new job starts
    // (e.g. after Clear Cache + regenerate).
    const initialSectionStatusRef = useRef<{
      jobId: string;
      statuses: Map<string, 'running' | 'completed'>;
    } | null>(null);
    const currentJobId = summaryResponse.summaryJobId;
    if (
      currentJobId !== undefined &&
      summaryResponse.summaries.length > 0 &&
      initialSectionStatusRef.current?.jobId !== currentJobId
    ) {
      const statuses = new Map<string, 'running' | 'completed'>();
      for (const summary of summaryResponse.summaries) {
        if (summary.status === 'completed' || summary.status === 'running') {
          statuses.set(summary.title, summary.status);
        }
      }
      initialSectionStatusRef.current = { jobId: currentJobId, statuses };
    }
    const initialSectionStatuses = initialSectionStatusRef.current?.statuses;

    // Memoize the callback to prevent new function reference on each render
    const handleAllContentPresented = useCallback(() => {
      setPresentedSummaryCount((prev) => prev + 1);
    }, []);

    // Kick off presenting summaries once the first is available. The presented count will later be
    // incremented as each summary finishes streaming
    useEffect(() => {
      telemetry.trackInteractionWithCondition('patient_summary_viewed', 'once_per_page_view', {
        summaryJobId: summaryResponse.summaryJobId,
        summaryJobStatus: summaryResponse.status,
      });

      for (let summaryIndex = 0; summaryIndex < presentedSummaryCount; summaryIndex++) {
        telemetry.trackInteractionWithCondition(
          'patient_summary_card_viewed',
          'once_per_page_view',
          {
            summaryJobId: summaryResponse.summaryJobId,
            summaryJobStatus: summaryResponse.status,
            summaryStatus: summaryResponse.summaries[summaryIndex]?.status,
            summaryIndex,
          },
          { summaryIndex },
        );
      }

      if (summaryResponse.summaries.length > 0 && presentedSummaryCount === 0) {
        setPresentedSummaryCount(1);
      }
    }, [telemetry, summaryResponse, presentedSummaryCount]);

    useEffect(() => {
      if (summaryResponse.status === 'insufficient-data') {
        telemetry.trackInteractionWithCondition(
          'patient_summary_insufficient_data',
          'once_per_page_view',
        );
      }
    }, [telemetry, summaryResponse.status]);

    useEffect(() => {
      if (summaryResponse.status === 'limit-reached') {
        telemetry.trackInteractionWithCondition(
          'patient_summary_limit_reached_no_cache',
          'once_per_page_view',
        );
      }
    }, [telemetry, summaryResponse.status]);

    useEffect(() => {
      if (summaryResponse.status === 'error') {
        telemetry.trackInteractionWithCondition(
          'patient_summary_error_state_presented',
          'once_per_page_view',
          { summaryJobId: summaryResponse.summaryJobId },
        );
      }
    }, [telemetry, summaryResponse.status, summaryResponse.summaryJobId]);

    if (isLoadingSummary) {
      return <LoadingSpinner centered={true} message="Loading Summary" />;
    }

    if (summaryResponse.status === 'insufficient-data') {
      return (
        <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
          <PatientSummarizationInsufficientDataBanner />
          <PatientSummaryInsufficientData />
        </PatientSummaryLayout>
      );
    }

    if (
      summaryResponse.status === 'limit-reached' ||
      (summaryResponse.summaries.length === 0 && summarizationLimitReached && !hasActiveJob)
    ) {
      // Either the api has told us we're cut off, or there's no summary yet
      // and we know we can't get one because we're at the limit
      return (
        <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
          <PatientSummarizationLimitReachedBanner
            variant="standalone"
            dismissable={false}
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
          <PatientSummaryLimitReached />
        </PatientSummaryLayout>
      );
    }

    if (summaryResponse.status === 'error') {
      return (
        <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
          <PatientSummarizationGenerationErrorBanner
            onTryAgain={handleGenerate}
            summaryJobId={summaryResponse.summaryJobId}
          />
          <PatientSummaryError />
        </PatientSummaryLayout>
      );
    }

    if (summaryResponse.summaries.length === 0) {
      // Either no job has been initiated or we're waiting for one to finish
      return (
        <PatientSummaryJobProvider summaryResponse={summaryResponse}>
          <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
            <div className={tw`flex flex-col gap-3 pt-3`}>
              <PatientSummarizationStatusCard
                patientSummarizationResult={hasActiveJob ? summaryResponse : null}
                onGenerateSummary={handleGenerate}
                hideUsageTicker={true}
                upsell={upsell}
              />
              {hasActiveJob ? <PatientSummaryGeneratingSkeleton /> : <PatientSummaryNoSummaryYet />}
            </div>
          </PatientSummaryLayout>
        </PatientSummaryJobProvider>
      );
    }

    return (
      <PatientSummaryLayout className={className} summaryResponse={summaryResponse}>
        {summarizationLimitReached ? (
          <PatientSummarizationLimitReachedBanner
            variant="standalone"
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
        ) : summarizationLimitApproaching ? (
          <PatientSummarizationLimitApproachingBanner
            variant="standalone"
            summaryJobId={summaryResponse.summaryJobId}
            summaryJobStatus={summaryResponse.status}
          />
        ) : null}
        <div className={tw`flex flex-col gap-3`}>
          {summaryResponse.summaries.map((summary, index) => (
            <PatientSummaryCard
              key={`summary-card-${summary.title}`}
              summaryResponse={summaryResponse}
              summaryIndex={index}
              enabled={presentedSummaryCount >= index + 1}
              streaming={initialSectionStatuses?.get(summary.title) !== 'completed'}
              onAllContentPresented={handleAllContentPresented}
            />
          ))}
        </div>
        <div className={tw`pt-6 text-xs italic`}>
          This health record summary is generated using AI technology and is intended for
          informational purposes only. While efforts have been made to ensure accuracy, the
          information provided may not capture all relevant details or updates.
        </div>
      </PatientSummaryLayout>
    );
  },
});
